import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Play, Check, Flame, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { VIDEOS } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";

const LESSONS = [
  { id: "l1", title: "Hot-Work Awareness & Fire Prevention", src: VIDEOS.firewatch, desc: "How hot work creates fire risk aboard ships and how a firewatch prevents it." },
  { id: "l2", title: "Hazard Recognition & PPE", src: VIDEOS.osha, desc: "Spotting hazards in confined shipyard spaces and the PPE that keeps you safe." },
  { id: "l3", title: "Fire Extinguisher & Emergency Response", src: VIDEOS.fire2, desc: "Extinguisher types, the PASS method, and clear shipyard communication." },
  { id: "l4", title: "Firewatch Responsibilities on the Job", src: VIDEOS.welding, desc: "Your duties before, during, and after hot work — and the fire watch hold." },
];

export default function FirewatchCourse({ open, onClose }) {
  const { user, completeFirewatch, openAuth } = useAuth();
  const [current, setCurrent] = useState(0);
  const [watched, setWatched] = useState({ 0: true });
  const [busy, setBusy] = useState(false);

  const done = !!user?.courses?.firewatch?.completed;
  const allWatched = LESSONS.every((_, i) => watched[i]);

  const markWatched = (i) => setWatched((w) => ({ ...w, [i]: true }));

  const complete = async () => {
    if (!user) { onClose(); openAuth("register"); toast.info("Create a free account to save your Firewatch completion."); return; }
    if (user.role !== "candidate") { toast.error("Switch to a worker account to take courses."); return; }
    setBusy(true);
    try {
      await completeFirewatch();
      toast.success("Firewatch course complete", { description: "Added to your trades passport. OSHA is unlocked." });
      onClose();
    } catch { toast.error("Could not save completion. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-[#10161F] border-white/10 text-[#F4F6F8] rounded-none p-0 gap-0 max-h-[92vh] overflow-y-auto [&>button]:hidden">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#10161F] z-10">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 grid place-items-center bg-[#FF5E1E] text-[#0B0F14]"><Flame size={22} /></span>
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">ENTRY COURSE · ~15 MIN</div>
              <DialogTitle className="font-display font-black uppercase text-xl tracking-tight">Firewatch Fundamentals</DialogTitle>
            </div>
          </div>
          <button data-testid="course-close-button" onClick={onClose} className="text-[#8FA3B8] hover:text-white"><X size={20} /></button>
        </div>

        <div className="grid md:grid-cols-5">
          {/* Player */}
          <div className="md:col-span-3 p-5">
            <video
              key={LESSONS[current].src}
              data-testid="course-video"
              src={LESSONS[current].src}
              controls
              autoPlay
              muted
              playsInline
              onPlay={() => markWatched(current)}
              className="w-full aspect-video bg-black"
            />
            <h3 className="mt-4 font-display font-bold uppercase text-lg">{current + 1}. {LESSONS[current].title}</h3>
            <p className="mt-1 text-sm text-[#8FA3B8]">{LESSONS[current].desc}</p>
            <p className="mt-3 text-[11px] text-[#8FA3B8] font-mono-tech tracking-wide">REPRESENTATIVE FOOTAGE · NOT A CERTIFICATION</p>
          </div>

          {/* Lesson list + complete */}
          <div className="md:col-span-2 p-5 border-t md:border-t-0 md:border-l border-white/10 flex flex-col">
            <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E] mb-3">LESSONS</div>
            <div className="space-y-1.5 flex-1">
              {LESSONS.map((l, i) => (
                <button
                  key={l.id}
                  data-testid={`course-lesson-${i}`}
                  onClick={() => { setCurrent(i); markWatched(i); }}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border transition-colors ${current === i ? "border-[#FF5E1E] bg-[#FF5E1E]/10" : "border-white/10 hover:border-white/25"}`}
                >
                  {watched[i] || done ? <CheckCircle2 size={16} className="text-[#10B981] shrink-0" /> : current === i ? <Play size={16} className="text-[#FF5E1E] shrink-0" /> : <Circle size={16} className="text-[#8FA3B8] shrink-0" />}
                  <span className="text-sm">{l.title}</span>
                </button>
              ))}
            </div>

            {done ? (
              <div className="mt-4 flex items-center justify-center gap-2 border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] font-mono-tech text-[11px] tracking-[0.15em] h-12">
                <Check size={16} /> COURSE COMPLETED
              </div>
            ) : (
              <Button data-testid="course-complete-button" onClick={complete} disabled={busy || (!allWatched && !!user)} className="mt-4 w-full rounded-none bg-[#10B981] hover:bg-[#0ea472] text-[#0B0F14] font-display font-bold uppercase h-12 disabled:opacity-50">
                <Check className="mr-2" size={18} /> {busy ? "Saving…" : "Mark Course Complete"}
              </Button>
            )}
            {!user && <p className="mt-2 text-[11px] text-[#8FA3B8] text-center">Sign in to save your completion to your passport.</p>}
            {user && !allWatched && !done && <p className="mt-2 text-[11px] text-[#8FA3B8] text-center">Play all {LESSONS.length} lessons to finish.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
